import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CFormSelect,
  CLoadingButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilCheckCircle,
  cilCloudDownload,
  cilCloudUpload,
  cilFile,
  cilSwapHorizontal,
  cilWarning,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  ImportErrorAlert,
  ImportFileBar,
  ImportUploadStep,
  InfoBox,
  MiniStepper,
  StatTile,
  tintStyle,
} from 'src/components/common/ImportWizard'
import {
  MATCH_META,
  buildPlayerIndex,
  normalize,
  readPlayerCells,
  resolvePlayer,
  takeColumn,
  takePlayerColumns,
} from 'src/components/common/playerMatching'
import CommonService from 'src/service/CommonService'
import PlayerService from 'src/service/PlayerService'

const WIZARD_STEPS = ['Upload File', 'Validate Players', 'Review & Import']

// One page big enough to hold a whole game's players, so validation can run offline.
const REFERENCE_LIMIT = 2000

// What the API reads as "clear this player's category".
const NONE = 'none'

const RESULT_META = {
  valid: { label: 'Valid', color: 'success', icon: cilCheckCircle, accent: '#198754' },
  unmatched_player: {
    label: 'Player Not Found',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  ambiguous_player: {
    label: 'Several Matches',
    color: 'warning',
    icon: cilWarning,
    accent: '#b45309',
  },
  missing_type: { label: 'Missing Type', color: 'danger', icon: cilWarning, accent: '#dc3545' },
  invalid_type: { label: 'Unknown Type', color: 'danger', icon: cilWarning, accent: '#dc3545' },
  type_not_allowed: {
    label: 'Type Not Used In Game',
    color: 'warning',
    icon: cilWarning,
    accent: '#b45309',
  },
  duplicate: { label: 'Duplicate Row', color: 'warning', icon: cilWarning, accent: '#b45309' },
}

const playerLabel = (player) =>
  player?.team_name ? `${player.full_name} (${player.team_name})` : player?.full_name || ''

// The file's type cell, or the admin's pick, turned into the one spelling the API is sent: the
// game's own tag name, or `none`. The API also knows every other active tag, but refuses all of
// them except the one this game uses - so those get their own result rather than "unknown".
const resolveType = (base, types) => {
  const raw = base.manual_type || base.csv_type
  const key = normalize(raw)
  if (!key) return { type: null, result: 'missing_type' }
  if (key === NONE) return { type: NONE }
  if (types.tag && key === normalize(types.tag.name)) return { type: types.tag.name }
  if (types.all.some((t) => normalize(t.name) === key)) {
    return { type: null, result: 'type_not_allowed' }
  }
  return { type: null, result: 'invalid_type' }
}

// Re-run over the whole file after every change - which player a row points at decides whether
// some other row is now a duplicate, so a single row cannot be re-checked on its own.
const buildValidatedRows = (baseRows, index, types) => {
  const claimed = new Set()

  return baseRows.map((base, i) => {
    const { record: player, match, ambiguous } = resolvePlayer(base, index)
    const { type, result: typeResult } = resolveType(base, types)

    let result = 'valid'
    if (!player) {
      result = ambiguous ? 'ambiguous_player' : 'unmatched_player'
    } else if (typeResult) {
      result = typeResult
    } else if (claimed.has(String(player.id))) {
      result = 'duplicate'
    }

    if (result === 'valid') claimed.add(String(player.id))

    const currentId = Number(player?.external_player_type) || 0
    const newId = type === NONE ? 0 : type ? types.tag.id : null

    return {
      ...base,
      row: i + 1,
      player_key: base.manual_id || '',
      player_id: player ? String(player.id) : '',
      player_name: player?.full_name ?? '',
      team_name: player?.team_name ?? '',
      match,
      ambiguous_count: ambiguous?.length ?? 0,
      type,
      current_label: player ? types.labelFor(currentId) : '',
      changed: player && newId !== null ? currentId !== newId : false,
      result,
    }
  })
}

// Player Category Restrictions import: upload a spreadsheet of players and the external
// category each one should carry, check every row against this game's players and its one
// allowed tag, fix any of it inline, then commit only the rows left included. Mirrors the other
// import wizards' 3-step shape, built on the shared ImportWizard pieces.
const ExternalPlayerImport = () => {
  const navigate = useNavigate()

  const [wizardStep, setWizardStep] = useState(1)
  const [maxWizardStep, setMaxWizardStep] = useState(1)

  const [fileName, setFileName] = useState('')
  const [fileRowCount, setFileRowCount] = useState(0)
  const [ignoredHeaders, setIgnoredHeaders] = useState([])
  const [busy, setBusy] = useState(false)

  const [optionsLoading, setOptionsLoading] = useState(true)
  const [players, setPlayers] = useState([])
  const [tagId, setTagId] = useState(0)
  const [typeList, setTypeList] = useState([])

  const [rows, setRows] = useState([])

  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])
  // The API carries on past a bad row, so a list of row errors means everything else was saved.
  const [partialImport, setPartialImport] = useState(false)

  useEffect(() => {
    setOptionsLoading(true)
    Promise.all([
      CommonService.gameStructureInfo(),
      PlayerService.getPlayers(0, REFERENCE_LIMIT, 1, new URLSearchParams()),
    ])
      .then(([gameRes, playerRes]) => {
        if (gameRes?.status === 200) {
          setTagId(Number(gameRes.game_data?.external_player_tag) || 0)
          setTypeList(gameRes.externalPlayerList || [])
        }
        setPlayers(playerRes?.data?.data || [])
      })
      .catch(() => ToastComponent('Could not load the players and categories.', 'error'))
      .finally(() => setOptionsLoading(false))
  }, [])

  const types = useMemo(() => {
    const tag = typeList.find((t) => Number(t.id) === tagId) || null
    return {
      tag,
      all: typeList,
      labelFor: (id) =>
        id ? typeList.find((t) => Number(t.id) === Number(id))?.name || 'Unknown' : 'None',
    }
  }, [typeList, tagId])

  const tagName = types.tag?.name || 'External'

  const typeOptions = useMemo(
    () => [
      { value: NONE, label: 'None' },
      ...(types.tag ? [{ value: types.tag.name, label: types.tag.name }] : []),
    ],
    [types],
  )

  const playerIndex = useMemo(
    () =>
      buildPlayerIndex(players, {
        getKey: (player) => player.id,
        getPlayerId: (player) => player.id,
        getName: (player) => player.full_name,
      }),
    [players],
  )

  const playerOptions = useMemo(
    () =>
      players
        .map((player) => ({ value: String(player.id), label: playerLabel(player) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [players],
  )

  const goToStep = (n) => {
    setWizardStep(n)
    setMaxWizardStep((m) => Math.max(m, n))
  }

  const resetAll = () => {
    setWizardStep(1)
    setMaxWizardStep(1)
    setFileName('')
    setFileRowCount(0)
    setIgnoredHeaders([])
    setRows([])
    setImportErrors([])
  }

  // Built from the players already loaded rather than a static sample: every player in the
  // game with the category they carry today, so the admin edits one column and uploads it back.
  const handleDownloadTemplate = () => {
    const header = ['pid', 'player_name', 'team_name', 'external_type']
    const body = [...players]
      .sort((a, b) => String(a.full_name).localeCompare(String(b.full_name)))
      .map((player) => {
        const current = Number(player.external_player_type) || 0
        return [
          player.id,
          player.full_name,
          player.team_name ?? '',
          current ? types.labelFor(current) : NONE,
        ]
      })
    const sheet = XLSX.utils.aoa_to_sheet([header, ...body])
    const book = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(book, sheet, 'Players')
    XLSX.writeFile(book, 'MyClubtap-ExternalPlayers.csv', { bookType: 'csv' })
  }

  const handleFile = (file) => {
    if (!file) return
    setBusy(true)
    const reader = new FileReader()
    reader.onload = (evt) => {
      let json = []
      let rawHeaders = []
      try {
        const isCsv = /\.csv$/i.test(file.name)
        const workbook = XLSX.read(evt.target.result, { type: isCsv ? 'string' : 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        json = XLSX.utils.sheet_to_json(sheet, { defval: '' })
        rawHeaders = json.length ? Object.keys(json[0]) : []
      } catch (err) {
        ToastComponent('Could not read this file. Please check the format and try again.', 'error')
        setBusy(false)
        return
      }

      if (!rawHeaders.length) {
        ToastComponent('This file has no rows to import.', 'error')
        setBusy(false)
        return
      }

      // The type and team columns are claimed first so `team_name` cannot be read as the
      // player's name, and the template's short `pid` header stands in for `Player ID`.
      const pool = [...rawHeaders]
      const typeCol = takeColumn(pool, ['externaltype', 'category', 'type', 'tag'])
      takeColumn(pool, ['teamcode'])
      takeColumn(pool, ['teamname', 'team'])
      const pidCol = takeColumn(pool, ['pid'])
      const playerColumns = takePlayerColumns(pool)
      if (pidCol && !playerColumns.playerIdCol) playerColumns.playerIdCol = pidCol

      if (!playerColumns.playerIdCol && !playerColumns.found) {
        ToastComponent('This file has no PID or player name column.', 'error')
        setBusy(false)
        return
      }
      if (!typeCol) {
        ToastComponent('This file has no external_type column.', 'error')
        setBusy(false)
        return
      }

      // Rows with no player on them are the blank lines spreadsheets leave behind, not data.
      const baseRows = json
        .map((r) => ({
          ...readPlayerCells(r, playerColumns),
          csv_type: String(r[typeCol] ?? '').trim(),
          manual_id: '',
          manual_type: '',
        }))
        .filter((r) => r.csv_name !== '' || r.csv_player_id !== '')

      if (!baseRows.length) {
        ToastComponent('This file has no rows to import.', 'error')
        setBusy(false)
        return
      }

      setFileName(file.name)
      setFileRowCount(baseRows.length)
      setIgnoredHeaders(pool)
      setImportErrors([])
      setRows(
        buildValidatedRows(baseRows, playerIndex, types).map((r) => ({
          ...r,
          included: r.result === 'valid',
        })),
      )
      setBusy(false)
    }
    if (/\.csv$/i.test(file.name)) reader.readAsText(file)
    else reader.readAsArrayBuffer(file)
  }

  const patchRow = (row, patch) => {
    setRows((prev) => {
      const baseRows = prev.map((r) => (r.row === row ? { ...r, ...patch } : r))
      // A row the admin has just changed comes back included; every other row keeps the
      // include/exclude decision already made for it.
      return buildValidatedRows(baseRows, playerIndex, types).map((r, idx) => ({
        ...r,
        included: r.result === 'valid' && (r.row === row || prev[idx].included),
      }))
    })
  }

  const remapPlayer = (row, playerKey) => patchRow(row, { manual_id: playerKey })
  const remapType = (row, type) => patchRow(row, { manual_type: type })

  const toggleRowIncluded = (row) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row === row && r.result === 'valid' ? { ...r, included: !r.included } : r,
      ),
    )
  }

  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.result === 'valid').length
    return { valid, needsAttention: rows.length - valid }
  }, [rows])

  const readyRows = rows.filter((r) => r.included)
  const changingRows = readyRows.filter((r) => r.changed).length

  const runImport = () => {
    setImporting(true)
    setImportErrors([])

    const payload = {
      players: readyRows.map((r) => ({ pid: r.player_id, external_type: r.type })),
    }

    // The API numbers its errors by position in what it was sent, not by file row, so they are
    // put back onto the row numbers the admin has been looking at.
    const toFileRow = (message) =>
      String(message).replace(/^Row (\d+):/, (whole, n) => {
        const source = readyRows[Number(n) - 1]
        return source ? `Row ${source.row}:` : whole
      })

    PlayerService.importExternalPlayers(payload)
      .then((res) => {
        if (res.success) {
          ToastComponent(res.message, 'success')
          navigate('/manage-external-players')
        } else {
          const rowErrors = Array.isArray(res.errors)
          setPartialImport(rowErrors)
          setImportErrors(
            rowErrors ? res.errors.map(toFileRow) : [res.message || 'Something went wrong.'],
          )
        }
      })
      .catch(() => {
        setPartialImport(false)
        setImportErrors(['Something went wrong. Please try again.'])
      })
      .finally(() => setImporting(false))
  }

  const renderUpload = () => (
    <>
      <ImportUploadStep
        templateHint="The template lists every player in this game with the category they have today. Change the external_type column and upload it back."
        templateButton={
          <CButton
            color="primary"
            variant="outline"
            disabled={optionsLoading || !players.length}
            onClick={handleDownloadTemplate}
          >
            <CIcon icon={cilCloudDownload} className="me-1" />
            Download Player Template
          </CButton>
        }
        requiredColumns={[
          <>
            PID <span className="text-medium-emphasis">(or player name)</span>
          </>,
          <>
            external_type{' '}
            <span className="text-medium-emphasis">
              ({types.tag ? `${tagName} or none` : 'none'})
            </span>
          </>,
        ]}
        info={
          types.tag || optionsLoading ? (
            <>
              This game uses the <strong>{tagName}</strong> category, so each row can set a player
              to <code>{tagName}</code> or clear it with <code>none</code>. Players left out of the
              file keep the category they have. Any other columns are ignored.
            </>
          ) : (
            <>
              This game has no external player category set, so a file can only clear players back
              to <code>none</code>. Choose the game&apos;s category first to mark players.
            </>
          )
        }
        fileName={fileName}
        fileDetail={`${fileRowCount} row${fileRowCount === 1 ? '' : 's'} detected`}
        busy={busy || optionsLoading}
        busyLabel={
          optionsLoading
            ? 'Loading the players and categories for this game…'
            : 'Reading and validating your file…'
        }
        disabled={optionsLoading}
        onFile={handleFile}
        onRemove={resetAll}
      />

      <div className="d-flex justify-content-between mt-4">
        <CButton
          color="secondary"
          variant="outline"
          onClick={() => navigate('/manage-external-players')}
        >
          Cancel
        </CButton>
        <CButton color="primary" disabled={!rows.length} onClick={() => goToStep(2)}>
          Review File
        </CButton>
      </div>
    </>
  )

  const renderValidate = () => (
    <>
      <ImportFileBar fileName={fileName} detail={`${fileRowCount} rows`} onReplace={resetAll} />

      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Valid" value={summary.valid} tone="success" icon={cilCheckCircle} />
        <StatTile
          label="Needs Attention"
          value={summary.needsAttention}
          tone="warning"
          icon={cilWarning}
        />
      </div>

      {ignoredHeaders.length > 0 && (
        <InfoBox color="light">
          Ignored column{ignoredHeaders.length === 1 ? '' : 's'}:{' '}
          <strong>{ignoredHeaders.join(', ')}</strong>. Only the player and external_type are read
          from this file.
        </InfoBox>
      )}

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 ge-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Row</CTableHeaderCell>
                <CTableHeaderCell>In File</CTableHeaderCell>
                <CTableHeaderCell style={{ minWidth: 260 }}>Player</CTableHeaderCell>
                <CTableHeaderCell>Current</CTableHeaderCell>
                <CTableHeaderCell style={{ minWidth: 160 }}>New Type</CTableHeaderCell>
                <CTableHeaderCell>Result</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rows.map((r) => {
                const meta = RESULT_META[r.result] || RESULT_META.valid
                const matchMeta = MATCH_META[r.match]
                return (
                  <CTableRow
                    key={r.row}
                    style={r.result === 'valid' ? undefined : tintStyle(meta.accent, '0d')}
                  >
                    <CTableDataCell className="ps-4 text-medium-emphasis">{r.row}</CTableDataCell>

                    <CTableDataCell>
                      <div className="fw-semibold">{r.csv_name || '—'}</div>
                      {r.csv_player_id && <code className="small">{r.csv_player_id}</code>}
                    </CTableDataCell>

                    <CTableDataCell>
                      <CFormSelect
                        size="sm"
                        value={r.player_key}
                        onChange={(e) => remapPlayer(r.row, e.target.value)}
                      >
                        {/* Clearing a row that was pointed somewhere by hand puts it back on
                            whatever the file matched by itself, rather than emptying it. */}
                        <option value="">
                          {r.manual_id
                            ? 'Undo manual mapping'
                            : r.player_id
                            ? playerLabel(playerIndex.byKey.get(r.player_id))
                            : 'Select player'}
                        </option>
                        {playerOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </CFormSelect>
                      {matchMeta && (
                        <CBadge
                          color={matchMeta.color}
                          shape="rounded-pill"
                          className="px-2 py-1 mt-1"
                        >
                          {matchMeta.label}
                        </CBadge>
                      )}
                      {r.result === 'ambiguous_player' && (
                        <div className="small text-medium-emphasis mt-1">
                          {r.ambiguous_count} players share this name — pick the right one.
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>{r.current_label || '—'}</CTableDataCell>

                    <CTableDataCell>
                      <CFormSelect
                        size="sm"
                        value={r.type ?? ''}
                        onChange={(e) => remapType(r.row, e.target.value)}
                      >
                        <option value="">Select type</option>
                        {typeOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </CFormSelect>
                      {r.csv_type && normalize(r.csv_type) !== normalize(r.type) && (
                        <div className="small text-medium-emphasis mt-1">
                          File says &ldquo;{r.csv_type}&rdquo;
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      <CBadge color={meta.color} shape="rounded-pill" className="px-2 py-1">
                        {meta.label}
                      </CBadge>
                      {r.result === 'valid' && !r.changed && (
                        <div>
                          <CBadge color="secondary" shape="rounded-pill" className="px-2 py-1 mt-1">
                            No change
                          </CBadge>
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      {r.result === 'valid' && (
                        <CButton
                          size="sm"
                          color={r.included ? 'danger' : 'success'}
                          variant="outline"
                          title={r.included ? 'Exclude from import' : 'Include in import'}
                          onClick={() => toggleRowIncluded(r.row)}
                        >
                          {r.included ? 'Exclude' : 'Include'}
                        </CButton>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      {summary.needsAttention > 0 && (
        <InfoBox color="light" className="mt-3">
          Rows that need attention are left out of the import. Pick the right player or type to fix
          a row, or leave it — the rest will still import.
        </InfoBox>
      )}

      <div className="d-flex justify-content-between mt-3">
        <CButton color="secondary" variant="outline" onClick={() => goToStep(1)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CButton color="primary" disabled={!readyRows.length} onClick={() => goToStep(3)}>
          Continue
        </CButton>
      </div>
    </>
  )

  const renderReview = () => (
    <>
      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="File Rows" value={rows.length} tone="secondary" icon={cilFile} />
        <StatTile
          label="Ready to Import"
          value={readyRows.length}
          tone="success"
          icon={cilCheckCircle}
        />
        <StatTile
          label="Excluded"
          value={rows.length - readyRows.length}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile label="Will Change" value={changingRows} tone="info" icon={cilSwapHorizontal} />
      </div>

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 ge-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">PID</CTableHeaderCell>
                <CTableHeaderCell>Player</CTableHeaderCell>
                <CTableHeaderCell>Team</CTableHeaderCell>
                <CTableHeaderCell>Category</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {readyRows.map((r) => {
                const newLabel = r.type === NONE ? 'None' : r.type
                return (
                  <CTableRow key={r.row}>
                    <CTableDataCell className="ps-4">
                      <code>{r.player_id}</code>
                    </CTableDataCell>
                    <CTableDataCell className="fw-semibold">{r.player_name}</CTableDataCell>
                    <CTableDataCell>{r.team_name || '—'}</CTableDataCell>
                    <CTableDataCell>
                      {r.changed ? (
                        <>
                          <span className="text-medium-emphasis">{r.current_label}</span>
                          <span className="mx-2">→</span>
                          <span className="fw-semibold">{newLabel}</span>
                        </>
                      ) : (
                        <span className="text-medium-emphasis">{newLabel} (no change)</span>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      <div className="d-flex justify-content-between mt-3">
        <CButton color="secondary" variant="outline" onClick={() => goToStep(2)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CLoadingButton
          color="success"
          loading={importing}
          disabled={!readyRows.length}
          onClick={runImport}
        >
          <CIcon icon={cilCloudUpload} className="me-1" />
          Import {readyRows.length} Player{readyRows.length === 1 ? '' : 's'}
        </CLoadingButton>
      </div>
    </>
  )

  return (
    <>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h4 className="fw-bold mb-0">Player Category Restrictions (PCR)</h4>
          <div className="small text-medium-emphasis">
            Set which players are {tagName} players from a spreadsheet.
          </div>
        </div>
        <CButton
          color="secondary"
          variant="outline"
          onClick={() => navigate('/manage-external-players')}
        >
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back to {tagName} Players
        </CButton>
      </div>
      <CCard className="mb-4 border-0 shadow-sm">
        <CCardBody>
          <MiniStepper
            steps={WIZARD_STEPS}
            current={wizardStep}
            maxReached={maxWizardStep}
            onStepClick={goToStep}
          />
          <ImportErrorAlert
            errors={importErrors}
            title={
              partialImport
                ? 'Some rows could not be imported - every other row was saved:'
                : undefined
            }
          />
          {wizardStep === 1 && renderUpload()}
          {wizardStep === 2 && renderValidate()}
          {wizardStep === 3 && renderReview()}
        </CCardBody>
      </CCard>
    </>
  )
}

export default ExternalPlayerImport
